import React from 'react';
import { Link } from 'react-router-dom';
import { LuBell, LuX } from 'react-icons/lu';
import {
  IconButton, Menu, Tooltip, Typography, Box, Button, Chip
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import FlagIcon from '@mui/icons-material/Flag';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { NOTIF_TYPE_CONFIG, getNotifUrl, timeAgo } from './notificationHelpers';
import { listNotifications, getUnreadCount, markRead, markAllRead } from '../../utils/notificationsApi';
import { acceptInvitation, declineInvitation } from '../../utils/groupsApi';

export default function NotificationsMenu({
  notifRef,
  notifAnchorEl,
  notifMenuOpen,
  setNotifAnchorEl,
  setNotifOpen,
  notifCount,
  setNotifCount,
  badgeCount,
  setBadgeCount,
  setBadgeCleared,
  notifications,
  setNotifications,
  dismissedNotifIds,
  setDismissedNotifIds,
  persistDismissedNotifs,
  processingInviteId,
  setProcessingInviteId,
  upcomingItems,
  dismissedKeys,
  setDismissedKeys,
  persistDismissed,
  buildKey,
  UPCOMING_DAYS,
  navigate,
  showSnack,
  t,
}) {
  const closeMenu = () => { setNotifAnchorEl(null); setNotifOpen(false); };

  return (
    <div ref={notifRef} style={{ position: 'relative' }}>
      <IconButton
        title={t('header.notificationsBellTitle', 'Notifications')}
        aria-label={t('header.notificationsBellTitle', 'Notifications')}
        onClick={async (e) => {
          if (notifMenuOpen) {
            closeMenu();
            return;
          }
          setNotifAnchorEl(e.currentTarget);
          setNotifOpen(true);
          setBadgeCount(0);
          setBadgeCleared(true);
          try {
            const data = await listNotifications({ unread: false, limit: 20 });
            const items = Array.isArray(data?.items) ? data.items : [];
            setNotifications(items.filter(n => n?.relatedType !== 'meeting'));
            const cnt = await getUnreadCount().catch(() => ({ count: 0 }));
            setNotifCount(cnt?.count || 0);
          } catch (_) {
            setNotifications([]);
          }
        }}
        sx={{ color: 'inherit !important' }}
      >
        <LuBell style={{ fontSize: '1.2rem' }} />
        {(notifCount + badgeCount) > 0 && (
          <span className="app-badge-count">
            {(notifCount + badgeCount) > 99 ? '99+' : (notifCount + badgeCount)}
          </span>
        )}
      </IconButton>

      <Menu
        anchorEl={notifAnchorEl}
        open={notifMenuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: 360, maxWidth: 400, maxHeight: { xs: '80vh', sm: 560 },
            overflowY: 'auto', overflowX: 'hidden', borderRadius: '16px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
            '& .MuiMenu-list': { display: 'block', p: 0 },
            '& li.MuiMenuItem-root': {
              display: 'flex', width: '100%', justifyContent: 'flex-start',
              alignItems: 'center', textAlign: 'left', whiteSpace: 'normal'
            }
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)', color: '#fff'
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {t('header.notifications.postNotification.title', 'Notifications')}
          </Typography>
          {notifications.filter(n => !dismissedNotifIds.has(n.id) && !n.read).length > 0 && (
            <Tooltip title={t('header.notifications.postNotification.markAllRead', 'Mark all as read')}>
              <IconButton size="small" sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                onClick={async () => {
                  try {
                    await markAllRead();
                    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                    setNotifCount(0);
                  } catch (_) {}
                }}>
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Notification items */}
        <Box sx={{ py: 0.5 }}>
          {notifications.filter(n => !dismissedNotifIds.has(n.id)).length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <LuBell style={{ fontSize: 32, color: '#d1d5db', marginBottom: 8 }} />
              <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                {t('header.notifications.postNotification.empty', 'No notifications')}
              </Typography>
            </Box>
          ) : (
            notifications.filter(n => !dismissedNotifIds.has(n.id)).map(n => {
              const cfg = NOTIF_TYPE_CONFIG[n.type] || NOTIF_TYPE_CONFIG.mention;
              const TypeIcon = cfg.icon;
              return (
                <Box key={n.id} sx={{
                  position: 'relative',
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  bgcolor: n.read ? 'transparent' : cfg.bg,
                  transition: 'background 0.2s',
                  '&:hover': { bgcolor: n.read ? 'rgba(0,0,0,0.02)' : cfg.bg }
                }}>
                  <Link
                    to={getNotifUrl(n)}
                    onClick={async () => {
                      closeMenu();
                      if (!n.read) {
                        try {
                          await markRead(n.id);
                          setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
                          const cnt = await getUnreadCount().catch(() => ({ count: 0 }));
                          setNotifCount(cnt?.count || 0);
                        } catch (_) {}
                      }
                    }}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 40px 12px 16px', color: 'inherit', textDecoration: 'none' }}
                  >
                    <Box sx={{
                      width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `linear-gradient(135deg, ${cfg.color}20, ${cfg.color}35)`,
                      color: cfg.color, mt: 0.25
                    }}>
                      <TypeIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', lineHeight: 1.3, color: 'text.primary' }}>
                        {n.sender?.username || t('posts.userFallback', 'User')}{' '}
                        <Typography component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                          {t(cfg.label, '')}
                        </Typography>
                      </Typography>
                      <Typography sx={{
                        fontSize: '0.82rem', color: 'text.secondary', mt: 0.25, lineHeight: 1.3,
                        overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                      }}>
                        {(() => {
                          const msg = n.message;
                          if (!msg) return n.title || '';
                          if (typeof msg === 'string' && msg.startsWith('i18n:')) return t(msg.slice(5), '');
                          if (msg === 'You were mentioned in a post') return t('notifications.mentionedInPost', 'You were mentioned in a post');
                          return msg;
                        })()}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                        {!n.read && (
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: cfg.color, flexShrink: 0 }} />
                        )}
                        <Typography sx={{ fontSize: '0.75rem', color: !n.read ? cfg.color : 'text.disabled', fontWeight: !n.read ? 600 : 400 }}>
                          {timeAgo(n.createdAt, t)}
                        </Typography>
                      </Box>
                      {/* Accept/Decline buttons for group invitations */}
                      {n.type === 'group_invite' && !n.read && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={processingInviteId === n.id}
                            sx={{ fontSize: '0.72rem', py: 0.25, px: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600, minWidth: 0, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                            onClick={async (e) => {
                              e.preventDefault(); e.stopPropagation();
                              setProcessingInviteId(n.id);
                              try {
                                await acceptInvitation(n.relatedId);
                                await markRead(n.id).catch(() => {});
                                setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
                                setNotifCount(c => Math.max(0, (c || 0) - 1));
                                showSnack(t('groups.invitation.accepted'), 'success');
                                closeMenu();
                                navigate(`/groups/${n.relatedId}`);
                              } catch (err) { showSnack(err.message || 'Failed', 'error'); }
                              setProcessingInviteId(null);
                            }}
                          >{t('groups.invitation.accept')}</Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={processingInviteId === n.id}
                            sx={{ fontSize: '0.72rem', py: 0.25, px: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600, minWidth: 0, color: '#ef4444', borderColor: '#ef4444', '&:hover': { borderColor: '#dc2626', bgcolor: 'rgba(239,68,68,0.06)' } }}
                            onClick={async (e) => {
                              e.preventDefault(); e.stopPropagation();
                              setProcessingInviteId(n.id);
                              try {
                                await declineInvitation(n.relatedId);
                                setNotifications(prev => prev.filter(x => x.id !== n.id));
                                if (!n.read) setNotifCount(c => Math.max(0, (c || 0) - 1));
                                showSnack(t('groups.invitation.declined'), 'info');
                              } catch (err) { showSnack(err.message || 'Failed', 'error'); }
                              setProcessingInviteId(null);
                            }}
                          >{t('groups.invitation.decline')}</Button>
                        </Box>
                      )}
                    </Box>
                  </Link>
                  {/* Dismiss button */}
                  <IconButton
                    size="small"
                    title={t('header.hideAlert')}
                    aria-label={t('header.hideAlert')}
                    sx={{ position: 'absolute', top: 8, right: 8, color: 'text.disabled', '&:hover': { color: 'text.secondary', bgcolor: 'rgba(0,0,0,0.06)' } }}
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        if (!n.read) {
                          setNotifCount(c => Math.max(0, (c || 0) - 1));
                          await markRead(n.id).catch(() => {});
                        }
                      } catch (_) {}
                      setNotifications(prev => prev.filter(x => x.id !== n.id));
                      setDismissedNotifIds(prev => {
                        const next = new Set(prev);
                        next.add(n.id);
                        persistDismissedNotifs(next);
                        return next;
                      });
                    }}
                  >
                    <LuX style={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
              );
            })
          )}
        </Box>

        {/* Upcoming section divider */}
        <Box sx={{
          px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1,
          bgcolor: 'rgba(0,0,0,0.02)', borderTop: '1px solid rgba(0,0,0,0.06)', borderBottom: '1px solid rgba(0,0,0,0.06)'
        }}>
          <FlagIcon sx={{ fontSize: 16, color: '#d97706' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary' }}>
            {t('header.upcomingTitle', { days: UPCOMING_DAYS })}
          </Typography>
        </Box>

        {/* Upcoming items */}
        <Box sx={{ py: 0.5 }}>
          {upcomingItems.filter(it => !dismissedKeys.has(buildKey(it))).length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>{t('header.noUpcoming')}</Typography>
            </Box>
          ) : (
            upcomingItems.filter(it => !dismissedKeys.has(buildKey(it))).map(item => {
              const isTask = item.type === 'task';
              const itemColor = isTask ? '#d97706' : '#059669';
              const ItemIcon = isTask ? ChecklistIcon : FlagIcon;
              return (
                <Box key={`${item.type}-${item.id}`} sx={{
                  position: 'relative', borderBottom: '1px solid rgba(0,0,0,0.04)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }, transition: 'background 0.2s'
                }}>
                  <Link
                    to={item.url}
                    onClick={closeMenu}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 40px 10px 16px', color: 'inherit', textDecoration: 'none' }}
                  >
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `linear-gradient(135deg, ${itemColor}20, ${itemColor}35)`,
                      color: itemColor
                    }}>
                      <ItemIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3 }}>
                        {item.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                        <Chip
                          size="small"
                          label={isTask ? t('common.task') : t('common.goal')}
                          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: `${itemColor}18`, color: itemColor, borderRadius: '6px' }}
                        />
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {t('common.due')} {new Date(item.date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Link>
                  <IconButton
                    size="small"
                    title={t('header.hideAlert')}
                    aria-label={t('header.hideAlert')}
                    sx={{ position: 'absolute', top: 6, right: 8, color: 'text.disabled', '&:hover': { color: 'text.secondary', bgcolor: 'rgba(0,0,0,0.06)' } }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDismissedKeys(prev => {
                        const next = new Set(prev);
                        next.add(buildKey(item));
                        persistDismissed(next);
                        return next;
                      });
                    }}
                  >
                    <LuX style={{ fontSize: 11 }} />
                  </IconButton>
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ px: 2, py: 1.5, textAlign: 'center', borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.01)' }}>
          <Link
            to="/planning"
            onClick={closeMenu}
            style={{ color: '#7c3aed', textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem' }}
          >
            {t('header.openPlanning')}
          </Link>
        </Box>
      </Menu>
    </div>
  );
}
